import {
  AccessOptionToShortenedReadableMap,
  getExperienceAccessOption,
  getOptionalThirdPartyDeliveryService,
  isTicketTypeDeliveryVariety,
  isTicketTypeEventVariety,
  isTicketTypePickupVariety,
  isTicketTypeTablesideVariety,
} from '@tock/shared/utils/offerings';
import { NormalizedTicketTypeVariety } from '@tock/shared/types/ticketPriceInformation';
import { core, safeEnumCompare } from '@tock/coretypes';
import { dashboard } from 'types';
import { guestGroupsByIdSelector } from 'selectors/guestGroupsSelectors';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import dashboardProtos from 'utils/protos';

const { TicketType } = dashboardProtos;

// these are the current ticket type varieties which we're expecting to have price configs in use.
export const ticketTypeVarietiesUsingPriceConfigs: core.TicketType.Variety[] = [
  'DEPOSIT',
  'FULL',
  'RESERVATION',
  'DEPRECATED_EVENT',
  'SEATED_EVENT',
  'GA_EVENT',
  'DEPRECATED_SPECIAL_TABLE',
  'ORDER_DELIVERY',
  'ORDER_PICKUP',
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sortOptionsAndOptionGroups(ticket: any) {
  const orderedOptionGroups = _.orderBy(
    ticket.optionGroup,
    ['isArchived', 'ordering'],
    ['asc', 'asc']
  );
  const sortedOptionGroups = _.map(orderedOptionGroups, (optionGroup, index) => {
    const orderedOptions = _.orderBy(
      optionGroup.ticketOption,
      ['isArchived', 'ordering'],
      ['asc', 'asc']
    );
    const sortedOptions = _.map(orderedOptions, (option, index) => {
      return {
        ...option,
        ordering: index,
      };
    });
    return {
      ...optionGroup,
      ticketOption: sortedOptions,
      ordering: index,
    };
  });

  return {
    ...ticket,
    optionGroup: sortedOptionGroups,
  };
}

export function optionExclusionMatcher(
  selectedExperience: core.ITicketType,
  selectedSupplementGroup?: core.ITicketOptionGroup
) {
  if (!_.isEmpty(selectedExperience) && selectedSupplementGroup) {
    const exclusions = selectedExperience.optionExclusion;
    const optionsInGroup = selectedSupplementGroup.ticketOption;
    if (optionsInGroup) {
      for (let i = 0; i < optionsInGroup.length; ++i) {
        const option = optionsInGroup[i];
        if (!_.isEmpty(_.filter(exclusions, { selectedOptionId: option.id }))) {
          return true;
        }
      }
    }
  }
  return false;
}

export function canAccrueLoyaltyPoints(
  ticketType?: core.TicketType | core.ITicketType | null
): boolean {
  return (
    !!ticketType &&
    !safeEnumCompare(ticketType.variety, TicketType.Variety, TicketType.Variety.RESERVATION)
  );
}

export function canRescheduleFromTo(
  ticketTypeFrom?: core.ITicketType,
  ticketTypeTo?: core.ITicketType
) {
  if (!ticketTypeFrom || !ticketTypeTo) {
    return false;
  }
  if (isTicketTypeEventVariety(ticketTypeFrom)) {
    return ticketTypeFrom.id === ticketTypeTo.id;
  } else if (isTicketTypeDeliveryVariety(ticketTypeFrom)) {
    if (getOptionalThirdPartyDeliveryService(ticketTypeFrom)) {
      return false;
    }
    return isTicketTypeDeliveryVariety(ticketTypeTo);
  } else if (isTicketTypePickupVariety(ticketTypeFrom)) {
    return isTicketTypePickupVariety(ticketTypeTo);
  } else if (isTicketTypeTablesideVariety(ticketTypeFrom)) {
    return false;
  } else {
    return (
      !isTicketTypeEventVariety(ticketTypeTo) &&
      !isTicketTypeDeliveryVariety(ticketTypeTo) &&
      !isTicketTypePickupVariety(ticketTypeTo)
    );
  }
}

export const sortTicketTypesAlphabetically = (a: core.TicketType, b: core.TicketType) => {
  const nameA = a.name.toUpperCase(); // ignore upper and lowercase
  const nameB = b.name.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }

  // names must be equal
  return 0;
};

export function getShortenedReadableExperienceAccessOption(
  experience: core.TicketType,
  guestGroupsById: Record<number, dashboard.GuestProfileGroup>
): string {
  const accessOption = getExperienceAccessOption(experience);
  const shortenedReadableAccessOption = AccessOptionToShortenedReadableMap[accessOption];

  if (accessOption === 'GUEST_GROUP') {
    // list group names
    return `${shortenedReadableAccessOption}: ${
      experience.guestProfileGroupId.length > 1
        ? 'Multiple groups'
        : experience.guestProfileGroupId
            .map((groupId) => guestGroupsById[groupId]?.name)
            .filter((name) => name) // remove empty values
            .join(', ')
    }`;
  }

  return shortenedReadableAccessOption;
}

export function useShortenedReadableExperienceAccessOption(experience: core.TicketType): string {
  const guestGroupsById = useSelector(guestGroupsByIdSelector);
  return getShortenedReadableExperienceAccessOption(experience, guestGroupsById);
}

export function varietyAndPriceToTicketTypePriceConfig(
  variety: NormalizedTicketTypeVariety,
  amountCents: number
): core.ITicketTypePriceConfig {
  // Default price config priceDistribution to PER_TICKET until we
  // implement actual values here.
  const priceDistribution = 'PER_TICKET';

  switch (variety) {
    case 'DEPOSIT':
      return { priceDistribution, priceType: 'DEPOSIT', priceCents: amountCents };

    case 'FULL':
      return { priceDistribution, priceType: 'PREPAID', priceCents: amountCents };

    case 'RESERVATION':
      return { priceDistribution, priceType: 'FREE' };
    case 'PAYMENT_CARD_HOLD':
      return { priceDistribution, priceType: 'PAYMENT_CARD_HOLD', priceCents: amountCents };
    default:
      return amountCents > 0
        ? { priceDistribution, priceType: 'PREPAID', priceCents: amountCents }
        : { priceDistribution, priceType: 'FREE', priceCents: amountCents };
  }
}
